import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { legacyApi, EstimateSummary } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { Button } from '../../../components/ui/button';
import { Plus, Pencil, Trash2, RotateCw, FileText, Eye, Download } from 'lucide-react';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { StandardActions } from '../components/StandardActions';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';
import { Badge } from '../../../components/ui/badge';
import { getEstimateRef } from '../../../utils/estimateRefs';

export const LegacyEstimates: React.FC = () => {
    const PAGE_SIZE = 50;
    const [estimates, setEstimates] = useState<EstimateSummary[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
    const [debouncedQ, setDebouncedQ] = useState('');
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '', status: 'all', dateRange: '|' });
    const searchTerm = state.q;
    const statusFilter = state.status;
    const dateRangeFilter = state.dateRange;

    useEffect(() => {
        const timeoutId = window.setTimeout(() => setDebouncedQ(searchTerm.trim()), 250);
        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);

    const loadEstimates = async (reset = true) => {
        if (reset) setLoading(true);
        else setLoadingMore(true);

        const currentOffset = reset ? 0 : estimates.length;
        const [start, end] = dateRangeFilter.split('|');
        try {
            const response = await legacyApi.estimates.listPaged({
                q: debouncedQ || undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                date_from: start || undefined,
                date_to: end || undefined,
                limit: PAGE_SIZE,
                offset: currentOffset,
            });
            setEstimates((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
            if (reset && !hasLoadedOnce) {
                setHasLoadedOnce(true);
            }
        }
    };

    useEffect(() => {
        loadEstimates(true);
    }, [debouncedQ, statusFilter, dateRangeFilter]);

    const getEstimateStatusLabel = (estimate: EstimateSummary) => {
        if (estimate.approval_status_id === 5) return 'Ordered';
        if (estimate.approval_status_id === 4) return 'Approved';
        if (estimate.approval_status_id === 3) return 'Sent';
        if (estimate.approval_status_id === 7) return 'Superseded';
        if (estimate.is_freezed) return 'Locked';
        return 'Draft';
    };

    const handleDelete = async (estimateId: number) => {
        try {
            await legacyApi.estimates.delete(estimateId);
            const remaining = estimates.filter(e => e.estimate_id !== estimateId);
            setEstimates(remaining);
            setTotalCount(prev => Math.max(prev - 1, 0));
            setShowDeleteConfirm(null);
        } catch (err) {
            console.error('Error deleting estimate:', err);
        }
    };

    const handleRevisions = (estimateId: number) => {
        navigate(`/legacy/estimates/${estimateId}/revisions`);
    };

    if (!hasLoadedOnce && loading) return <div className="p-8 text-center text-muted-foreground">Loading Estimates...</div>;

    const getStatusBadge = (estimate: EstimateSummary) => {
        const label = getEstimateStatusLabel(estimate);
        let color = 'bg-slate-500';

        if (label === 'Ordered') {
            color = 'bg-green-600';
        } else if (label === 'Approved') {
            color = 'bg-emerald-500';
        } else if (label === 'Sent') {
            color = 'bg-blue-600';
        } else if (label === 'Superseded') {
            color = 'bg-amber-600';
        } else if (label === 'Locked') {
            color = 'bg-red-600';
        }

        // List view should show a single latest status (like classic grid).
        return (
            <span className={`px-2 py-1 rounded text-xs ${color} text-white font-medium inline-block w-20 text-center`}>
                {label}
            </span>
        );
    };

    const columns = [
        { header: "ID", accessor: (e: EstimateSummary) => getEstimateRef(e) || e.estimate_no || e.estimate_id.toString(), className: 'font-mono w-36 whitespace-nowrap' },
        { header: "Rev", accessor: (e: EstimateSummary) => e.revision_no.toString(), className: 'w-10 text-center' },
        {
            header: "Inquiry",
            accessor: (e: EstimateSummary) => e.inquiry_id ? (
                <Link
                    to={`/legacy/inquiries/${e.inquiry_id}`}
                    onClick={(ev) => ev.stopPropagation()}
                    className="text-blue-600 hover:underline font-mono"
                >
                    {`HC/ENQ/${e.inquiry_id.toString().padStart(4, '0')}`}
                </Link>
            ) : '-',
            className: 'w-24 text-center whitespace-nowrap'
        },
        { header: "Date", accessor: (e: EstimateSummary) => e.date_created ? new Date(e.date_created).toLocaleDateString() : '-', className: 'w-20 whitespace-nowrap' },
        { header: "Project Type", accessor: (e: EstimateSummary) => e.project_type || '-', className: 'whitespace-nowrap w-20' },
        {
            header: "Customer",
            accessor: (e: EstimateSummary) => (
                <div className="flex items-center gap-2">
                    <span className="truncate">{e.customer_name || e.customer_name_override || '-'}</span>
                    {e.entry_mode === 'quick_quote' ? <Badge variant="outline">Quick Quote</Badge> : null}
                </div>
            ),
            className: 'min-w-[140px] max-w-[220px] truncate'
        },
        { header: "Project Description", accessor: (e: EstimateSummary) => e.project_description || '-', className: 'min-w-[120px] max-w-[180px] truncate' },
        { header: "Total SQM", accessor: (e: EstimateSummary) => (e.total_sqm ?? 0).toFixed(2), className: 'w-16 text-right tabular-nums' },
        { header: "AVG Price / SQM", accessor: (e: EstimateSummary) => (e.avg_price_per_sqm ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), className: 'w-20 text-right tabular-nums' },
        { header: "Total Price", accessor: (e: EstimateSummary) => (e.total_price ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), className: 'w-20 text-right font-medium tabular-nums' },
        { header: "Net Profit", accessor: (e: EstimateSummary) => (e.net_profit ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), className: 'w-16 text-right tabular-nums text-green-600' },
        { header: "Status", accessor: (e: EstimateSummary) => getStatusBadge(e), className: 'w-16 text-center' },
        {
            header: "Actions",
            accessor: (e: EstimateSummary) => (
                <StandardActions
                    view={{
                        title: "View Estimate",
                        onClick: (ev) => {
                            ev.stopPropagation();
                            navigate(`/legacy/estimates/${e.estimate_id}`);
                        },
                        icon: <Eye className="w-4 h-4 text-green-600" />
                    }}
                    edit={{
                        title: "Edit Estimate",
                        disabled: !!e.is_freezed || e.approval_status_id === 4,
                        onClick: (ev) => {
                            ev.stopPropagation();
                            navigate(`/legacy/estimates/${e.estimate_id}?tab=inputs`);
                        },
                        icon: <Pencil className="w-4 h-4 text-blue-600" />
                    }}
                    download={{
                        title: "Download PDF",
                        onClick: async (ev) => {
                            ev.stopPropagation();
                            await legacyApi.estimates.downloadPDF(e.estimate_id);
                        },
                        icon: <Download className="w-4 h-4 text-purple-600" />
                    }}
                    remove={{
                        title: "Delete Estimate",
                        onClick: (ev) => {
                            ev.stopPropagation();
                            setShowDeleteConfirm(e.estimate_id);
                        },
                        icon: <Trash2 className="w-4 h-4 text-red-600" />
                    }}
                    overflow={[
                        {
                            label: "View Revisions",
                            onClick: (ev) => {
                                ev.stopPropagation();
                                handleRevisions(e.estimate_id);
                            },
                            icon: <FileText className="w-4 h-4 text-green-600" />
                        }
                    ]}
                />
            ),
            className: 'text-right sticky right-0 bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)] z-10 pr-2'
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Estimates</h1>
                    <p className="text-muted-foreground">
                        Manage historical estimate records - Showing {estimates.length} of {totalCount} items
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadEstimates(true)}
                        title="Refresh data"
                    >
                        <RotateCw className="mr-2 h-4 w-4" />
                        Refresh
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => navigate('/legacy/estimates/quick-quote')}
                    >
                        <FileText className="mr-2 h-4 w-4" />
                        Quick Quotation
                    </Button>
                    <Button
                        onClick={async () => {
                            const confirmed = await confirm("Estimates must be generated from an Inquiry. Go to Inquiries?", {
                                confirmText: "Go to Inquiries",
                                cancelText: "Cancel"
                            });
                            if (confirmed) {
                                navigate('/legacy/inquiries');
                            }
                        }}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        New Estimate
                    </Button>
                </div>
            </div>

            <div className="space-y-4">
                <FlowEntryBanner
                    title="Sales Path Guidance"
                    pathLabel="Inquiry -> Estimate"
                    nextHint="Approve/submit estimates, then convert to Sale Order for execution."
                />
                <TableFilterBar
                    searchValue={searchTerm}
                    onSearchChange={(value) => setFilter('q', value)}
                    searchPlaceholder="Search by ID, Customer, Project Type, or Description..."
                    controls={[
                        {
                            key: 'dateRange',
                            label: 'Date',
                            type: 'daterange',
                            value: dateRangeFilter,
                            onChange: (value) => setFilter('dateRange', value)
                        },
                        {
                            key: 'status',
                            label: 'Status',
                            type: 'select',
                            value: statusFilter,
                            onChange: (value) => setFilter('status', value),
                            options: [
                                { label: 'All Statuses', value: 'all' },
                                { label: 'Draft', value: 'draft' },
                                { label: 'Sent', value: 'sent' },
                                { label: 'Approved', value: 'approved' },
                                { label: 'Ordered', value: 'ordered' },
                                { label: 'Superseded', value: 'superseded' },
                                { label: 'Locked', value: 'locked' },
                            ],
                        },
                    ]}
                    isDirty={isDirty}
                    onClear={clearAll}
                    rightSlot={(
                        <div className="flex items-center gap-3">
                            <span>{estimates.length} of {totalCount} results</span>
                        </div>
                    )}
                />

                <div className="min-h-[400px] overflow-x-auto">
                    <CompactTable
                        data={estimates}
                        columns={columns}
                        keyField="estimate_id"
                        loading={loading}
                        onRowClick={(item) => navigate(`/legacy/estimates/${item.estimate_id}`)}
                    />

                    {hasMore && (
                        <div className="flex justify-center mt-4">
                            <Button variant="ghost" onClick={() => loadEstimates(false)} disabled={loadingMore}>
                                {loadingMore ? "Loading..." : "Load older estimates"}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-background border rounded-lg shadow-lg p-6 max-w-sm">
                        <h2 className="text-lg font-bold mb-4">Confirm Delete</h2>
                        <p className="text-muted-foreground mb-6">
                            Are you sure you want to delete estimate #{showDeleteConfirm}? This action cannot be undone.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => setShowDeleteConfirm(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => handleDelete(showDeleteConfirm)}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
